import React, { useState, useEffect, useRef } from 'react';
import './AAccettazione.css';
import API from '../../api/API';
import TokenService from '../../api/tokenService';
import Pannello from './accettazione/Pannello';
import PannelloCibo from './accettazione/PannelloCibo';
import PannelloPersoneNuove from './accettazione/PannelloPersoneNuove';
import PannelloDistribuzioni from './accettazione/PannelloDistribuzioni';

/**
 * Accettazione squadre.
 *
 * Il caricamento e' in due tempi. La tabella chiede il riepilogo dell'edizione: una riga per
 * squadra, coi conteggi dei giocatori al posto delle distinte e un booleano al posto del logo.
 * La distinta di una squadra arriva quando quella squadra viene aperta, una richiesta per scheda,
 * e resta in cache per le riaperture. I loghi sono <img> verso l'endpoint immagine, in miniatura:
 * li carica il browser, solo per le righe che si vedono, e li tiene in cache.
 *
 * Prima si scaricava tutto in una risposta sola - ogni logo in base64 a piena risoluzione e, per
 * ogni giocatore, codice censimento, codice fiscale, sesso e data di nascita - per disegnare una
 * tabella che di quei dati mostra due conteggi e un cerchio da 40 pixel.
 */

/**
 * Il lato della miniatura chiesta all'endpoint del logo: nella tabella si vede in un cerchio da
 * 40 pixel, il doppio per gli schermi a densita' alta. Il file pieno - fino a 800x800 - resta a
 * chi apre il logo o lo scarica.
 */
const MINIATURA = 80;

/**
 * Gli accompagnatori dichiarati in iscrizione da una squadra. Il ripiego a zero non e' paranoia:
 * le due immagini si pubblicano una per volta, quindi un frontend nuovo puo' trovarsi davanti un
 * backend che nella riga il campo non lo mette ancora, e senza questo i totali direbbero NaN.
 */
const accompagnatoriDi = (squadra) => squadra.accompagnatori ?? 0;

/**
 * Il tooltip del contatore degli accompagnatori: la colonna mostra un numero e una lettera, il
 * significato lo dice qui.
 */
const etichettaAccompagnatori = (quanti) =>
  quanti === 1 ? '1 accompagnatore' : `${quanti} accompagnatori`;

/**
 * Ripiego di copiaNegliAppunti per gli host non sicuri, dove navigator.clipboard non esiste
 * (undefined, non un rifiuto): document.execCommand('copy') su un textarea temporaneo, invisibile
 * e fuori dal flusso. Ritorna true/false, non lancia mai - execCommand puo' lanciare o restituire
 * false a seconda del browser, e qui non deve fermare il chiamante in nessuno dei due casi.
 */
const copiaConExecCommand = (testo) => {
  const area = document.createElement('textarea');
  area.value = testo;
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.focus();
  area.select();
  let riuscito = false;
  try {
    riuscito = document.execCommand('copy');
  } catch {
    riuscito = false;
  }
  document.body.removeChild(area);
  return riuscito;
};

/**
 * Copia negli appunti con ripiego, perche' su HTTP semplice - il docker-compose_local.yml per
 * l'uso in sede che CLAUDE.md documenta, dietro il nginx che in environment/nginx.conf ascolta
 * solo su :80 - navigator.clipboard non esiste affatto: non rifiuta, e' undefined, e chiamarci
 * sopra .writeText lancerebbe dentro il gestore del click senza che nessuno lo veda. Risolve
 * true se il testo e' finito negli appunti con un metodo o l'altro, false se nessuno dei due ha
 * funzionato: chi chiama decide cosa mostrare quando anche il ripiego fallisce.
 */
const copiaNegliAppunti = (testo) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(testo).then(() => true, () => copiaConExecCommand(testo));
  }
  return Promise.resolve(copiaConExecCommand(testo));
};

const AccettazioneAdmin = () => {
  const [edizioneSelezionata, setEdizioneSelezionata] = useState(null);
  const [squadre, setSquadre] = useState([]);
  // I numeri dei pannelli. Arrivano da una seconda richiesta che parte insieme al riepilogo: la
  // tabella non la aspetta, e finche' non c'e' i pannelli dicono che stanno caricando.
  const [analisi, setAnalisi] = useState(null);
  const [analisiRotta, setAnalisiRotta] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [distinta, setDistinta] = useState(null);
  const [erroreDistinta, setErroreDistinta] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [teamForQuotaEdit, setTeamForQuotaEdit] = useState(null);
  const [newQuota, setNewQuota] = useState('');
  const [showMovePlayerModal, setShowMovePlayerModal] = useState(false);
  const [playerToMove, setPlayerToMove] = useState(null);
  const [targetTeam, setTargetTeam] = useState('');
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [loghiRotti, setLoghiRotti] = useState(() => new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  // L'id della squadra il cui link e' appena finito negli appunti: l'icona diventa una spunta
  // per un secondo. Non si riusa il trucco di handleContactCopy, che sostituisce il testo
  // dell'elemento: su un pulsante a icona cancellerebbe l'icona.
  const [linkCopiato, setLinkCopiato] = useState(null);

  // Le distinte gia' lette, per id squadra: riaprire una scheda non torna sulla rete.
  const distinteCaricate = useRef(new Map());
  // La scheda aperta adesso: una risposta che arriva tardi, quando l'utente ha gia' cambiato
  // squadra, non deve finire nel modal sbagliato.
  const schedaAperta = useRef(null);

  useEffect(() => {
    const storedEdition = localStorage.getItem('selectedEdition');
    if (storedEdition) {
      const edizione = JSON.parse(storedEdition);
      setEdizioneSelezionata(edizione);

      API.accettazione.riepilogo(edizione.id).then(res => {
        if(res.isSuccess){
          setSquadre(res.data || []);
          setLoading(false);
        }
        else {
          setLoading(false);
        }
      });

      // In parallelo, e non in coda: i pannelli sono chiusi, la tabella e' quello che si guarda
      // per primo, e nessuna delle due richieste deve aspettare l'altra.
      API.accettazione.analisi(edizione.id).then(res => {
        if (res.isSuccess) setAnalisi(res.data);
        else setAnalisiRotta(true);
      });
    } else {
      setLoading(false);
    }
  }, []);


  const handleTeamClick = (squadra) => {
    setSelectedTeam(squadra);
    setShowTeamModal(true);
    caricaDistinta(squadra.id);
  };

  /**
   * Mescola l'esito della richiesta del codice in una scheda gia' esistente. Un 403 e' un
   * "niente permesso" stabile: si scrive codice: null e si tiene per buono. Qualunque altro
   * fallimento - 500, rete che singhiozza - e' transitorio: non si scrive la chiave 'codice'
   * affatto, cosi' chi legge la cache sa che non e' un verdetto e puo' ritentare. Confonderli
   * vorrebbe dire che un guasto passeggero diventa un "permesso mancante" per tutta la sessione.
   */
  const conCodice = (scheda, resCodice) => {
    if (resCodice.isSuccess) return { ...scheda, codice: resCodice.data?.codice };
    if (resCodice.status === 403) return { ...scheda, codice: null };
    return scheda;
  };

  /**
   * La distinta di una squadra e il codice che apre il suo modulo in modifica, chiesti insieme.
   * Il codice serve al bottone Modifica, e va chiesto adesso e non al click: window.open dopo un
   * await lo blocca il browser. In cache va un oggetto solo, sotto l'id della squadra.
   */
  const caricaDistinta = (idSquadra) => {
    schedaAperta.current = idSquadra;
    setErroreDistinta(false);

    const inCache = distinteCaricate.current.get(idSquadra);
    if (inCache) {
      setDistinta(inCache);
      // 'codice' manca solo se l'ultima richiesta e' fallita per un motivo diverso dal 403:
      // si ritenta alla riapertura, invece di restare bloccati su un guasto che nel frattempo
      // potrebbe essersi gia' richiuso
      if (!('codice' in inCache)) {
        API.accettazione.codiceModifica(edizioneSelezionata.id, idSquadra).then(resCodice => {
          if (schedaAperta.current !== idSquadra) return;
          const aggiornata = conCodice(inCache, resCodice);
          if (aggiornata !== inCache) {
            distinteCaricate.current.set(idSquadra, aggiornata);
            setDistinta(aggiornata);
          }
        });
      }
      return;
    }

    setDistinta(null);
    Promise.all([
      API.accettazione.squadra(edizioneSelezionata.id, idSquadra),
      API.accettazione.codiceModifica(edizioneSelezionata.id, idSquadra)
    ]).then(([res, resCodice]) => {
      if (schedaAperta.current !== idSquadra) return;

      if (res.isSuccess && res.data) {
        const scheda = conCodice(res.data, resCodice);
        distinteCaricate.current.set(idSquadra, scheda);
        setDistinta(scheda);
      } else {
        setErroreDistinta(true);
      }
    });
  };

  const chiudiTeamModal = () => {
    schedaAperta.current = null;
    setShowTeamModal(false);
    setDistinta(null);
    setErroreDistinta(false);
  };

  const handleDeleteClick = (squadra) => {
    setTeamToDelete(squadra);
    setShowDeleteModal(true);
  };

  const handleContactCopy = (e, contatto) => {
    e.preventDefault();
    e.stopPropagation();
    if (!contatto) return;
    const el = e.currentTarget;
    copiaNegliAppunti(contatto).then(riuscito => {
      if (riuscito) {
        const original = el.getAttribute('data-original-text') || el.textContent;
        el.setAttribute('data-original-text', original);
        el.textContent = 'Copiato!';
        setTimeout(() => { el.textContent = original; }, 1000);
      } else {
        // ne' la Clipboard API ne' execCommand hanno funzionato: non deve restare in silenzio
        window.prompt('Copia il contatto:', contatto);
      }
    });
  };

  /**
   * Copia il link con cui la squadra corregge la propria distinta. E' quello pubblico, senza
   * codice: vale fino a chiusuraModifiche, e negli appunti non finisce niente di segreto.
   */
  const copiaLink = (squadra) => {
    if (!squadra.hash || !edizioneSelezionata) return;
    const link = `${window.location.origin}/tornei/iscrizione/${edizioneSelezionata.id}/${squadra.hash}`;
    copiaNegliAppunti(link).then(riuscito => {
      if (riuscito) {
        setLinkCopiato(squadra.id);
        setTimeout(() => setLinkCopiato(null), 1000);
      } else {
        // ne' la Clipboard API ne' execCommand hanno funzionato: non deve restare in silenzio
        window.prompt('Copia il link:', link);
      }
    });
  };

  const confirmDeleteTeam = () => {
    if (teamToDelete) {
      API.iscrizioni.cancella(teamToDelete.id, TokenService.getId()).then(res => {
        if(res.isSuccess) {
          setSquadre(prev => prev.filter(sq => sq.id !== teamToDelete.id));
          distinteCaricate.current.delete(teamToDelete.id);
          setShowDeleteModal(false);
          setTeamToDelete(null);
        } else {
          alert('Errore durante l\'eliminazione della squadra');
        }
      });
    }
  };

  const cancelDeleteTeam = () => {
    setShowDeleteModal(false);
    setTeamToDelete(null);
  };

  const handleQuotaClick = (squadra, e) => {
    e.preventDefault();
    e.stopPropagation();
    setTeamForQuotaEdit(squadra);
    setNewQuota(squadra.quota ? squadra.quota.toString() : '0');
    setShowQuotaModal(true);
  };

  const handleSaveQuota = () => {
    const quotaValue = parseFloat(newQuota);
    if (!isNaN(quotaValue) && quotaValue > 0 && teamForQuotaEdit) {
      API.accettazione.quota(teamForQuotaEdit.id, quotaValue).then(res => {
        if(res.isSuccess) {
          setSquadre(prev => prev.map(sq =>
            sq.id === teamForQuotaEdit.id ? { ...sq, quota: quotaValue } : sq
          ));
          if (selectedTeam && selectedTeam.id === teamForQuotaEdit.id) {
            setSelectedTeam(prev => ({ ...prev, quota: quotaValue }));
          }
          // anche la distinta in cache porta la quota: senza questo, riaprendo la scheda
          // tornerebbe quella vecchia
          const inCache = distinteCaricate.current.get(teamForQuotaEdit.id);
          if (inCache) {
            const aggiornata = { ...inCache, quota: quotaValue };
            distinteCaricate.current.set(teamForQuotaEdit.id, aggiornata);
            setDistinta(prev => (prev && prev.id === teamForQuotaEdit.id ? aggiornata : prev));
          }
          setShowQuotaModal(false);
          setTeamForQuotaEdit(null);
          setNewQuota('');
        } else {
          alert('Errore durante l\'aggiornamento della quota');
        }
      });
    }
  };

  const handleCancelQuota = () => {
    setShowQuotaModal(false);
    setTeamForQuotaEdit(null);
    setNewQuota('');
  };

  /** Riscrive i conteggi di una riga quando la sua distinta cambia sotto le mani. */
  const aggiornaConteggi = (idSquadra, sesso, delta) => {
    setSquadre(prev => prev.map(sq => {
      if (sq.id !== idSquadra) return sq;
      const s = (sesso || '').toUpperCase();
      return {
        ...sq,
        giocatori: sq.giocatori + delta,
        maschi: sq.maschi + (s === 'M' ? delta : 0),
        femmine: sq.femmine + (s === 'F' ? delta : 0)
      };
    }));
  };

  const handleDeletePlayer = (playerId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo giocatore?')) {
      const giocatore = distinta.giocatori.find(g => g.id === playerId);
      const aggiornata = { ...distinta, giocatori: distinta.giocatori.filter(g => g.id !== playerId) };

      distinteCaricate.current.set(distinta.id, aggiornata);
      setDistinta(aggiornata);
      aggiornaConteggi(distinta.id, giocatore?.sesso, -1);

      // Qui dovresti anche fare una chiamata API per eliminare il giocatore dal database
      // API.accettazione.deletePlayer(playerId).then(...)
    }
  };

  const handleMovePlayer = (player) => {
    setPlayerToMove(player);
    setShowMovePlayerModal(true);
    setTargetTeam('');
  };

  const confirmMovePlayer = () => {
    if (!targetTeam || !playerToMove || !distinta) return;

    const idDestinazione = parseInt(targetTeam);
    const destTeam = squadre.find(sq => sq.id === idDestinazione);
    if (!destTeam) return;

    // Rimuovi il giocatore dalla distinta di origine
    const origine = { ...distinta, giocatori: distinta.giocatori.filter(g => g.id !== playerToMove.id) };
    distinteCaricate.current.set(distinta.id, origine);
    setDistinta(origine);

    // La distinta della destinazione, se l'avevamo letta, ora non e' piu' quella giusta: si
    // butta e alla prossima apertura si rilegge.
    distinteCaricate.current.delete(idDestinazione);

    aggiornaConteggi(distinta.id, playerToMove.sesso, -1);
    aggiornaConteggi(idDestinazione, playerToMove.sesso, +1);

    setShowMovePlayerModal(false);
    setPlayerToMove(null);
    setTargetTeam('');

    // Qui dovresti anche fare una chiamata API per spostare il giocatore
    // API.accettazione.movePlayer(playerToMove.id, destTeam.id).then(...)
  };

  const calculateTotal = (quota, numGiocatori) => {
    return quota * numGiocatori;
  };

  const calculateAge = (dataNascita) => {
    const today = new Date();
    const birthDate = new Date(dataNascita);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  // Funzione per ottenere le squadre dello stesso torneo (escludendo quella corrente)
  const getAvailableTeamsForMove = () => {
    return squadre.filter(sq => sq.id !== selectedTeam?.id);
  };

  // Il logo si mostra se la riga dice che c'e' e se il browser e' riuscito a caricarlo.
  const haLogo = (squadra) => squadra.logo && !loghiRotti.has(squadra.id);

  // Gli accompagnatori di tutte le squadre dell'edizione: il terzo numero della testata.
  const totaleAccompagnatori = squadre.reduce((acc, sq) => acc + accompagnatoriDi(sq), 0);

  const logoRotto = (idSquadra) => {
    setLoghiRotti(prev => {
      const aggiornati = new Set(prev);
      aggiornati.add(idSquadra);
      return aggiornati;
    });
  };

  // Funzione per gestire il click sul logo
  const handleLogoClick = (e, squadra) => {
    e.stopPropagation();
    if (haLogo(squadra)) {
      setSelectedLogo({
        url: API.squadra.logoUrl(squadra.id),
        teamName: squadra.nome
      });
      setShowLogoModal(true);
    }
  };

  // Funzione per convertire il nome della squadra in CamelCase
  const toCamelCase = (str) => {
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Rimuove accenti
      .replace(/[^a-zA-Z0-9\s]/g, '') // Rimuove caratteri speciali
      .split(' ')
      .map((word, index) =>
        index === 0
          ? word.toLowerCase()
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join('');
  };

  if (loading) {
    return (
      <div className="acceptance-page">
        <div className="acceptance-loading">
          <div className="loading-spinner"></div>
          <p>Caricamento in corso...</p>
        </div>
      </div>
    );
  }

  if (!edizioneSelezionata) {
    return (
      <div className="acceptance-page">
        <div className="acceptance-container">
          <div className="acceptance-header">
            <div className="acceptance-title">
              <i className="fas fa-clipboard-check"></i>
              <h1>Accettazione Squadre</h1>
            </div>
          </div>
          <div className="no-edition-message">
            <i className="fas fa-info-circle"></i>
            <h3>Nessuna edizione selezionata</h3>
            <p>Seleziona un'edizione dalla pagina Tornei per visualizzare le squadre.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
      <div className="acceptance-container">
        <div className="acceptance-header">
          <div className="acceptance-title">
            <i className="fas fa-clipboard-check"></i>
            <h1>Accettazione Squadre</h1>
          </div>
          <div className="acceptance-stats">
            <div className="acceptance-stat">
              <i className="fas fa-users"></i>
              <span className="acceptance-counter-text">Quote: </span>
              <span className="acceptance-counter">{squadre.reduce((acc, sq) => acc + sq.giocatori * sq.quota, 0)}</span>
            </div>
            <div className="acceptance-stat">
              <i className="fas fa-users"></i>
              <span className="acceptance-counter-text">Squadre: </span>
              <span className="acceptance-counter">{squadre.length}</span>
            </div>
            <div className="acceptance-stat">
              <i className="fas fa-male"></i>
              <span className="acceptance-counter-text">Giocatori: </span>
              <span className="acceptance-counter">{squadre.reduce((acc, sq) => acc + sq.maschi, 0)} - {squadre.reduce((acc, sq) => acc + sq.femmine, 0)}</span>
              <span
                className="companion-count"
                title={etichettaAccompagnatori(totaleAccompagnatori)}
              >
                {totaleAccompagnatori}A
              </span>
            </div>
          </div>
        </div>

        {edizioneSelezionata && squadre.length > 0 && (
          <div className="acceptance-teams-section">
            <Pannello
              icona="fa-users"
              titolo="Squadre"
              aperto
              sommario={`${squadre.length} squadre iscritte`}
            >
              <div className="teams-table-container">
                <table className="teams-table">
                  <thead>
                    <tr>
                      <th>Logo</th>
                      <th>Codice</th>
                      <th>Nome</th>
                      <th>Gruppo Scout</th>
                      <th>Giocatori</th>
                      <th>Quota</th>
                      <th>Contatto</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {squadre.map(squadra => {
                      const total = calculateTotal(squadra.quota, squadra.giocatori);

                      return (
                        <tr
                          key={squadra.id}
                          className="team-row"
                          onClick={(e) => {
                            // Non aprire il modal se si clicca sulla quota, sul contatto o sulle azioni
                            if (e.target.closest('.quota-clickable') || e.target.closest('.team-actions') || e.target.closest('.contact-info')) {
                              e.stopPropagation();
                              return;
                            }
                            handleTeamClick(squadra);
                          }}
                        >
                          <td className="team-logo">
                            {haLogo(squadra) ? (
                              <img
                                src={API.squadra.logoUrl(squadra.id, MINIATURA)}
                                alt={`Logo ${squadra.nome}`}
                                className="logo-thumbnail logo-clickable"
                                loading="lazy"
                                decoding="async"
                                onError={() => logoRotto(squadra.id)}
                                onClick={(e) => handleLogoClick(e, squadra)}
                                title="Clicca per visualizzare il logo"
                              />
                            ) : (
                              <div className="logo-placeholder">
                                <i className="fas fa-image"></i>
                              </div>
                            )}
                          </td>
                          <td className="team-code">
                            <strong>{squadra.id}</strong>
                          </td>
                          <td className="team-name">
                            <strong>{squadra.nome}</strong>
                          </td>
                          <td className="team-gruppo">{squadra.gruppoScout || '-'}</td>
                          <td>
                            <div className="gender-count">
                              <span className="male-count">{squadra.maschi}M</span>
                              <span className="female-count">{squadra.femmine}F</span>
                              <span
                                className="companion-count"
                                title={etichettaAccompagnatori(accompagnatoriDi(squadra))}
                              >
                                {accompagnatoriDi(squadra)}A
                              </span>
                            </div>
                          </td>
                          <td className="quota-info">
                            <span
                              className="quota-detail quota-clickable"
                              onClick={(e) => handleQuotaClick(squadra, e)}
                              title="Clicca per modificare la quota"
                            >
                              <strong>€{total}</strong> (€{squadra.quota})
                            </span>
                          </td>
                          <td
                            className="contact-info"
                            onClick={(e) => handleContactCopy(e, squadra.contatto)}
                            style={{ cursor: squadra.contatto ? 'pointer' : 'default' }}
                            title={squadra.contatto ? 'Clicca per copiare' : ''}
                          >
                            {squadra.contatto}
                          </td>
                          <td className="team-actions" onClick={(e) => e.stopPropagation()}>
                            <button
                              className="btn btn-copy-link btn-sm"
                              onClick={() => copiaLink(squadra)}
                              disabled={!squadra.hash}
                              title="Copia il link di modifica"
                            >
                              <i className={`fas ${linkCopiato === squadra.id ? 'fa-check' : 'fa-link'}`}></i>
                            </button>
                            <button
                              className="btn btn-delete btn-sm"
                              onClick={() => handleDeleteClick(squadra)}
                              title="cancella squadra"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Pannello>

            {/* I tre pannelli arrivano con la seconda richiesta: finche' non c'e', lo dicono. */}
            {!analisi && !analisiRotta && (
              <p className="pannello-attesa">Sto caricando cibo, persone nuove e distribuzioni...</p>
            )}
            {analisiRotta && (
              <p className="pannello-vuoto">
                I pannelli non si sono caricati. La tabella qui sopra e' completa lo stesso.
              </p>
            )}
            {analisi && (
              <>
                {/* Dove non si mangia, il pannello del cibo non esiste proprio. */}
                {analisi.cibo && <PannelloCibo squadre={analisi.squadre} />}
                <PannelloPersoneNuove squadre={analisi.squadre} />
                <PannelloDistribuzioni
                  squadre={analisi.squadre}
                  gruppi={analisi.gruppi}
                  eta={analisi.eta}
                />
              </>
            )}
          </div>
        )}

        {/* Modal per visualizzare i giocatori */}
        {showTeamModal && selectedTeam && (
          <div className="modal-overlay" onClick={chiudiTeamModal}>
            <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header team-modal-header">
                <div className="team-header-row">
                  <span className="team-code-badge">#{selectedTeam.id}</span>
                  <h3 className="team-name-header">{selectedTeam.nome}</h3>

                  <div className="info-box players-total">
                    <span className="total-amount">{selectedTeam.giocatori}</span>
                    <span className="info-label-small">giocatori</span>
                  </div>
                  <div className="info-box total-quota">
                    <span className="total-amount">€{calculateTotal(selectedTeam.quota, selectedTeam.giocatori)}</span>
                    <span className="info-label-small">totale</span>
                  </div>
                  <div className="header-actions">
                    {/* a edizione chiusa il modulo non si apre piu' nemmeno per l'accettazione:
                        lo dice il server con modificabile, e qui al posto del bottone si scrive
                        perche'. Il codice invece dice se chi guarda ha il permesso di scrivere:
                        se manca (403, confermato) lo slot lo dice, non resta muto - altrimenti chi
                        ha perso il permesso non lo distingue da "e' rotto" o "la rete singhiozza" */}
                    {distinta && !distinta.modificabile ? (
                      <span className="modifiche-chiuse" title="L'edizione è chiusa: la distinta non è più modificabile">
                        <i className="fas fa-lock"></i>
                        Modifiche chiuse
                      </span>
                    ) : distinta && 'codice' in distinta && !distinta.codice ? (
                      <span className="modifiche-chiuse" title="Il tuo account ha solo permessi di lettura su questa edizione">
                        <i className="fas fa-user-lock"></i>
                        Serve il permesso di scrittura per aprire la distinta in modifica
                      </span>
                    ) : (
                      distinta?.codice && (
                        <button
                          className="btn btn-primary btn-edit-team"
                          onClick={() => {
                            window.open(
                              `/tornei/iscrizione/${edizioneSelezionata.id}/${distinta.hash}?admin=${encodeURIComponent(distinta.codice)}`,
                              '_blank'
                            );
                          }}
                          title="Modifica squadra"
                        >
                          <i className="fas fa-edit"></i>
                          Modifica
                        </button>
                      )
                    )}
                    <button
                      className="modal-close"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        chiudiTeamModal();
                      }}
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-body">
                {erroreDistinta && (
                  <div className="no-edition-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    <p>Non e' stato possibile caricare la distinta della squadra.</p>
                  </div>
                )}

                {!erroreDistinta && !distinta && (
                  <div className="acceptance-loading">
                    <div className="loading-spinner"></div>
                    <p>Caricamento distinta...</p>
                  </div>
                )}

                {distinta && (
                <div className="players-section">
                  {/* Vista desktop - tabella */}
                  <div className="players-table-desktop">
                    <table className="players-table">
                      <thead>
                        <tr>
                          <th>Codice</th>
                          <th>Nome</th>
                          <th>Cognome</th>
                          <th>Sesso</th>
                          <th>N</th>
                          <th>Maglia</th>
                          <th>Data di nascita</th>
                          <th>Gruppo</th>
                          <th>Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {distinta.giocatori.map(giocatore => (
                          <tr key={giocatore.id}>
                            <td className="jersey-number">{giocatore.cod}</td>
                            <td>{giocatore.nome}</td>
                            <td>{giocatore.cognome}</td>
                            <td>
                              <span className={`gender-badge ${giocatore.sesso.toLowerCase()}`}>
                                {giocatore.sesso}
                              </span>
                            </td>
                            <td>{giocatore.n}</td>
                            <td>{giocatore.maglia}</td>
                            <td>{giocatore.dataDiNascita}</td>
                            <td>{giocatore.gruppo}</td>
                            <td className="player-actions">
                              <button
                                onClick={() => handleMovePlayer(giocatore)}
                                className="btn btn-warning btn-xs"
                                title="Sposta in altra squadra"
                              >
                                <i className="fas fa-exchange-alt"></i>
                              </button>
                              <button
                                onClick={() => handleDeletePlayer(giocatore.id)}
                                className="btn btn-danger btn-xs"
                                title="Elimina giocatore"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Vista mobile - card */}
                  <div className="players-scroll-mobile">
                    {distinta.giocatori.map(giocatore => (
                      <div key={giocatore.id} className="player-card">
                        <div className="player-main-info">
                          <span className="player-name">{giocatore.nome} {giocatore.cognome}</span>
                          <span className="player-jersey">#{giocatore.n}</span>
                        </div>
                        <div className="player-details">
                          <span><strong>Sesso:</strong> {giocatore.sesso}</span>
                          <span><strong>Età:</strong> {calculateAge(giocatore.dataDiNascita)}</span>
                          <span><strong>Gruppo:</strong> {giocatore.gruppo}</span>
                          <div className="player-actions">
                            <button
                              onClick={() => handleMovePlayer(giocatore)}
                              className="btn btn-warning btn-xs"
                              title="Sposta in altra squadra"
                            >
                              <i className="fas fa-exchange-alt"></i>
                            </button>
                            <button
                              onClick={() => handleDeletePlayer(giocatore.id)}
                              className="btn btn-danger btn-xs"
                              title="Elimina giocatore"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal per spostare giocatore */}
        {showMovePlayerModal && playerToMove && (
          <div className="modal-overlay" onClick={() => setShowMovePlayerModal(false)}>
            <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Sposta Giocatore</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowMovePlayerModal(false)}
                  type="button"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p><strong>Giocatore:</strong> {playerToMove.nome} {playerToMove.cognome}</p>
                <p><strong>Squadra attuale:</strong> {selectedTeam.nome}</p>

                <div className="form-group">
                  <label htmlFor="targetTeam">Sposta in squadra:</label>
                  <select
                    id="targetTeam"
                    value={targetTeam}
                    onChange={(e) => setTargetTeam(e.target.value)}
                  >
                    <option value="">Seleziona squadra di destinazione</option>
                    {getAvailableTeamsForMove().map(team => (
                      <option key={team.id} value={team.id}>
                        {team.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="modal-actions">
                  <button
                    onClick={confirmMovePlayer}
                    className="btn btn-primary"
                    disabled={!targetTeam}
                  >
                    <i className="fas fa-check"></i>
                    Conferma Spostamento
                  </button>
                  <button
                    onClick={() => setShowMovePlayerModal(false)}
                    className="btn btn-secondary"
                  >
                    <i className="fas fa-times"></i>
                    Annulla
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal per visualizzare il logo */}
        {showLogoModal && selectedLogo && (
          <div className="modal-overlay" onClick={() => setShowLogoModal(false)}>
            <div className="modal-content logo-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Logo {selectedLogo.teamName}</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowLogoModal(false)}
                  type="button"
                >
                  ×
                </button>
              </div>
              <div className="modal-body logo-modal-body">
                <div className="logo-container">
                  <img
                    src={selectedLogo.url}
                    alt={`Logo ${selectedLogo.teamName}`}
                    className="logo-full"
                  />
                  {/* stesso indirizzo della miniatura: il browser ce l'ha gia' in cache, il
                      download non ripassa dalla rete. Il modulo di iscrizione accetta solo PNG. */}
                  <a
                    className="download-btn"
                    href={selectedLogo.url}
                    download={`${toCamelCase(selectedLogo.teamName)}.png`}
                    title="Scarica immagine"
                  >
                    <i className="fas fa-download"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal per modifica quota */}
        {showQuotaModal && teamForQuotaEdit && (
          <div className="modal-overlay" onClick={handleCancelQuota}>
            <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Modifica Quota - {teamForQuotaEdit.nome}</h3>
                <button
                  className="modal-close"
                  onClick={handleCancelQuota}
                  type="button"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="quota-edit-row">
                  <div className="form-group">
                    <label htmlFor="newQuota">Quota per giocatore (€):</label>
                    <input
                      id="newQuota"
                      type="number"
                      value={newQuota}
                      onChange={(e) => setNewQuota(e.target.value)}
                      className="quota-input"
                      step="1"
                      min="0"
                      placeholder="Inserisci nuova quota"
                    />
                  </div>
                  <div className="quota-preview">
                    <div className="preview-item">
                      <span className="preview-label">Giocatori:</span>
                      <span className="preview-value">{teamForQuotaEdit.giocatori}</span>
                    </div>
                    <div className="preview-item">
                      <span className="preview-label">Totale:</span>
                      <span className="preview-value total">€{(parseFloat(newQuota) * teamForQuotaEdit.giocatori || 0)}</span>
                    </div>
                  </div>
                </div>
                <div className="modal-actions">
                  <button
                    onClick={handleSaveQuota}
                    className="btn btn-primary"
                    disabled={!newQuota || parseFloat(newQuota) <= 0}
                  >
                    <i className="fas fa-check"></i>
                    Salva Quota
                  </button>
                  <button
                    onClick={handleCancelQuota}
                    className="btn btn-secondary"
                  >
                    <i className="fas fa-times"></i>
                    Annulla
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal per conferma eliminazione squadra */}
        {showDeleteModal && teamToDelete && (
          <div className="modal-overlay" onClick={cancelDeleteTeam}>
            <div className="modal-content modal-small delete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header delete-header">
                <div className="delete-icon">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <h3>Conferma Eliminazione</h3>
                <button
                  className="modal-close"
                  onClick={cancelDeleteTeam}
                  type="button"
                >
                  ×
                </button>
              </div>
              <div className="modal-body delete-body">
                <div className="delete-warning">
                  <p><strong>Sei sicuro di voler eliminare la squadra:</strong></p>
                  <div className="team-info-delete">
                    <table className="team-row-compact">
                      <td className="team-logo">
                        {haLogo(teamToDelete) ? (
                          <img
                            src={API.squadra.logoUrl(teamToDelete.id, MINIATURA)}
                            alt={`Logo ${teamToDelete.nome}`}
                            className="logo-thumbnail"
                            onError={() => logoRotto(teamToDelete.id)}
                          />
                        ) : (
                          <div className="logo-placeholder">
                            <i className="fas fa-image"></i>
                          </div>
                        )}
                      </td>
                      <td className="team-name">
                        <strong>{teamToDelete.nome}</strong>
                      </td>
                      <td className="team-players">
                        {teamToDelete.giocatori} giocatori
                      </td>
                      <td className="team-code">
                        <strong>#{teamToDelete.id}</strong>
                      </td>
                    </table>
                  </div>
                </div>
                <div className="modal-actions delete-actions">
                  <button
                    onClick={confirmDeleteTeam}
                    className="btn btn-danger btn-confirm-delete"
                  >
                    <i className="fas fa-trash"></i>
                    Elimina Squadra
                  </button>
                  <button
                    onClick={cancelDeleteTeam}
                    className="btn btn-secondary btn-cancel-delete"
                  >
                    <i className="fas fa-times"></i>
                    Annulla
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default AccettazioneAdmin;
